import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  CircularProgress,
  Button,
  Chip,
  TextField,
  InputAdornment,
  alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { colors, shadows } from "../source/source.styles";
import PhoneIcon from "@mui/icons-material/Phone";
import ChatIcon from "@mui/icons-material/Chat";
import SearchIcon from "@mui/icons-material/Search";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { getRecentDroppedApi } from "../../../api/leads.api";

const RecentDropped = ({ limit = 5, dateRange }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [droppedData, setDroppedData] = useState([]);

  // Create a stable key for date range to trigger updates
  const dateRangeKey = useMemo(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      return `${dateRange.startDate.getTime()}-${dateRange.endDate.getTime()}`;
    }
    return "";
  }, [dateRange]);

  useEffect(() => {
    const fetchRecentDropped = async () => {
      try {
        setLoading(true);
        const params = { limit };
        if (dateRange?.startDate && dateRange?.endDate) {
          params.startDate = dateRange.startDate.toISOString();
          params.endDate = dateRange.endDate.toISOString();
        }
        const response = await getRecentDroppedApi(params);
        // Response structure: { data: { data: [...], total: 5 } }
        const data = response?.data?.data?.data || [];
        setDroppedData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching recent dropped:", err);
        setDroppedData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentDropped();
  }, [limit, dateRangeKey]);

  // Filter data based on search query with safety check
  const filteredDropped = Array.isArray(droppedData)
    ? droppedData.filter(
        (dropped) =>
          dropped?.leadName
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          dropped?.source?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          dropped?.treatmentType
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          dropped?.telecaller?.name
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          dropped?.status?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          dropped?.dropReason
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()),
      )
    : [];

  const headerCellSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    color: colors.textMuted,
    textTransform: "uppercase",
    borderBottom: `1px solid ${colors.borderLight}`,
    py: 2,
    bgcolor: colors.bgPrimary,
  };

  const bodyCellSx = {
    fontSize: 13,
    fontWeight: 500,
    color: colors.textPrimary,
    borderBottom: `1px solid ${colors.borderLight}`,
    py: 2.5,
  };

  const getAvatarColor = (name) => {
    const avatarColors = [
      "#C4B5FD",
      "#FCA5A5",
      "#A7F3D0",
      "#FDE68A",
      "#BAE6FD",
    ];
    const index = name.charCodeAt(0) % avatarColors.length;
    return avatarColors[index];
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: "#FEE2E2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TrendingDownIcon sx={{ fontSize: 20, color: "#EF4444" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: colors.textPrimary,
              }}
            >
              Recent Dropped
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: colors.textSecondary,
              }}
            >
              Latest leads that were dropped
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            placeholder="Search..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 200,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 13,
                bgcolor: colors.bgPrimary,
                "& fieldset": {
                  borderColor: colors.borderLight,
                },
                "&:hover fieldset": {
                  borderColor: colors.border,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 1,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 1,
              },
            }}
          />
        </Stack>
      </Stack>

      {/* Table */}
      {loading ? (
        <Box
          sx={{
            py: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={32} sx={{ color: colors.primary }} />
        </Box>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Date</TableCell>
              <TableCell sx={headerCellSx}>Lead Name</TableCell>
              <TableCell sx={headerCellSx}>Contact</TableCell>
              <TableCell sx={headerCellSx}>Treatment</TableCell>
              <TableCell sx={headerCellSx}>Telecaller</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDropped.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 8, textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: colors.textMuted,
                      mb: 1,
                    }}
                  >
                    No dropped leads found
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: colors.textSecondary,
                    }}
                  >
                    {searchQuery
                      ? `No dropped leads matching "${searchQuery}"`
                      : "No recent dropped leads available"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredDropped.map((dropped, index) => (
                <TableRow
                  key={index}
                  onClick={() => navigate(`/pipelines/${dropped.id}`)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: colors.bgPrimary,
                    },
                  }}
                >
                  <TableCell sx={bodyCellSx}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: colors.textSecondary,
                      }}
                    >
                      {dropped.date}
                    </Typography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: getAvatarColor(dropped.leadName),
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {dropped.initials}
                      </Avatar>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: colors.textPrimary,
                        }}
                      >
                        {dropped.leadName}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: colors.bgPrimary,
                          "&:hover": { bgcolor: colors.border },
                        }}
                      >
                        <PhoneIcon
                          sx={{ fontSize: 14, color: colors.textMuted }}
                        />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: colors.bgPrimary,
                          "&:hover": { bgcolor: colors.border },
                        }}
                      >
                        <ChatIcon
                          sx={{ fontSize: 14, color: colors.textMuted }}
                        />
                      </IconButton>
                    </Stack>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {(dropped.treatments || [dropped.treatmentType]).map(
                        (treatment, idx) => (
                          <Chip
                            key={idx}
                            label={
                              treatment
                                ? treatment.charAt(0).toUpperCase() +
                                  treatment.slice(1)
                                : "N/A"
                            }
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: 11,
                              fontWeight: 600,
                              bgcolor: alpha(theme.palette.primary.main, 0.15),
                              color: theme.palette.primary.main,
                            }}
                          />
                        ),
                      )}
                    </Stack>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: 11,
                          bgcolor: colors.primary,
                        }}
                      >
                        {dropped.telecaller.name.charAt(0)}
                      </Avatar>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: colors.textPrimary,
                        }}
                      >
                        {dropped.telecaller.name}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      {/* Footer */}
      {!loading && droppedData.length > 0 && (
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${colors.borderLight}`,
            bgcolor: colors.bgPrimary,
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: colors.textSecondary,
              textAlign: "center",
            }}
          >
            Showing {filteredDropped.length} of {droppedData.length} recent
            dropped leads
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RecentDropped;
